/**
 * The OPOC.us token values this application consumes.
 *
 * Every entry is transcribed once, from `tokens.css` — the brand skill's generated file, copied
 * verbatim into this directory. Nothing else in the codebase types a colour: components read
 * `token()` (a CSS custom property with the value as its fallback, so a re-theme via the stylesheet
 * still works) or `literal()` (the raw value, for the places that must be parsed rather than
 * rendered).
 *
 * Two consumers need the literal rather than the custom property:
 *   - Material UI's palette, which computes light/dark/contrast variants and cannot parse `var()`.
 *   - `tint()`, which needs the channel values to produce an alpha wash.
 *
 * Those two are also the reason `DARK_TOKENS` exists. `tokens.css` already swaps the custom
 * properties under `[data-theme="dark"]`, so anything styled with `token()` follows the colour
 * scheme on its own; the literals have to be told.
 *
 * When the brand file changes, re-copy `tokens.css` and update this table to match. `tokens.css` is
 * the guide's published file, byte for byte (OPOC.us brand system 1.0.0, `tokens/tokens.css`,
 * sha256 737a0f21…); `npm run check:brand` runs the skill's own checker over `src/`, which fails
 * on any hex that is not here.
 */

export const TOKENS = {
  // brand anchors
  'color-brand-navy': '#13294B',
  'color-brand-blue': '#003C97',
  'color-brand-cyan': '#00B5E2',
  'color-brand-violet': '#2E008B',
  'color-brand-green': '#007A53',
  'color-brand-red': '#E02424',
  'color-brand-yellow': '#F3F160',

  // surfaces
  'color-surface-page': '#FFFFFF',
  'color-surface-raised': '#F8F8F8',
  'color-surface-sunken': '#F2F2F2',
  'color-surface-inverse': '#141414',
  'color-surface-inverse-brand': '#13294B',

  // semantic roles
  'color-semantic-ink': '#141414',
  'color-semantic-ink-muted': 'rgba(20,20,20,0.5)',
  'color-semantic-ink-muted-aa': 'rgba(20,20,20,0.6)',
  'color-semantic-ink-subtle': 'rgba(20,20,20,0.3)',
  'color-semantic-ink-on-dark': '#FFFFFF',
  // The navy rail is a brand surface in both colour schemes, so its ink never flips. This is
  // deliberately *not* `ink-on-dark`, which the brand inverts under a dark scheme because "on dark"
  // there means a light chip. White on #13294B is 14.05:1 either way.
  'color-semantic-ink-on-brand': '#FFFFFF',
  // The interactive blue is invisible on navy, so the rail takes the brand's dark focus ring.
  'color-semantic-focus-ring-on-brand': '#2CCDFD',
  'color-semantic-interactive': '#003C97',
  'color-semantic-interactive-hover': '#194FA1',
  'color-semantic-border-hairline': 'rgba(20,20,20,0.1)',
  'color-semantic-border-strong': '#E8E8E8',
  'color-semantic-focus-ring': '#003C97',

  // data visualisation — five categorical slots, taken in order and never cycled
  'color-dataviz-categorical-light-1': '#0149B3',
  'color-dataviz-categorical-light-2': '#00B5E2',
  'color-dataviz-categorical-light-3': '#007A53',
  'color-dataviz-categorical-light-4': '#5121DB',
  'color-dataviz-categorical-light-5': '#E02424',

  // emphasis — the recommended option. A fill behind ink, never text, a line or a border.
  'color-dataviz-emphasis-fill': '#F3F160',
  'color-dataviz-emphasis-tint': '#FAFBB1',
  'color-dataviz-emphasis-tint-edge': '#DCDB73',
  'color-dataviz-emphasis-ink': '#141414',

  // status — reserved: never a chart series, always shipped with an icon and a label
  'color-dataviz-status-light-good': '#007A53',
  'color-dataviz-status-light-warning': '#FFD100',
  'color-dataviz-status-light-serious': '#CB7E03',
  'color-dataviz-status-light-critical': '#E02424',

  /**
   * Critical as small text, which the brand set does not have a role for.
   *
   * `status.critical` is a *mark* colour. #E02424 clears 3:1 against both grounds, which is the bar
   * for a border, an icon or a chip edge — but as 13–14px text it measures 3.9:1 on #141414 and
   * 4.45:1 on the #F8F8F8 zebra row, so it fails AA in both schemes. Overdue dates are exactly that.
   *
   * This role takes the negative end of the brand's diverging ramp instead: the deep step on light
   * (6.3:1) and the pale step on dark (9.2:1). Both are brand values, and both still read as red.
   *
   * App-scoped tokens are not in `tokens.css` — `theme.ts` declares them on `:root` so `token()`
   * resolves them and they follow the colour scheme with everything else.
   */
  'color-app-critical-text': '#BC0210',
  /**
   * The tool sidebar: brand navy on light, the deep sunken navy on dark — the guide's own
   * `--app-side` (assets/opoc.css). Not `surface-inverse-brand`, which the guide lifts to #314B73
   * under a dark scheme for a navy *field* on a page; the sidebar sits darker than the page.
   */
  'color-app-side': '#13294B',

  // geometry
  'radius-sm': '10px',
  'radius-md': '16px',
  'radius-lg': '24px',
  'radius-xl': '30px',
  'radius-pill': '9999px',

  // line spacing (brand: typography and layout)
  'typography-line-height-tight': '1.05',
  'typography-line-height-snug': '1.2',
  'typography-line-height-normal': '1.45',

  // motion (brand: motion). Leaving is quicker than arriving.
  'motion-fast': '150ms',
  'motion-base': '250ms',
  'motion-slow': '500ms',
  'motion-easing': 'cubic-bezier(0.4, 0, 0.2, 1)',
  /** Something arriving slows into place. */
  'motion-easing-enter': 'cubic-bezier(0, 0, 0.2, 1)',
  /** Something leaving speeds away, and is shorter than its entrance. */
  'motion-easing-exit': 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

export type TokenName = keyof typeof TOKENS;

export type ColorMode = 'light' | 'dark';

/**
 * The values `tokens.css` swaps in under `:root[data-theme="dark"]`, transcribed here for the two
 * consumers that cannot read a custom property.
 *
 * These are the brand's own dark values — *selected* for a dark surface, not an inversion of the
 * light ones. Inverting programmatically fails the contrast gates, which is why this is a table and
 * not a function. Anything absent here is the same in both schemes: the yellow emphasis fill and
 * its dark ink, the navy rail, the geometry.
 *
 * This table must stay in step with the `[data-theme="dark"]` block of `tokens.css`;
 * `tokens.test.ts` parses that file and fails if the two disagree.
 */
export const DARK_TOKENS = {
  // Deep navy derived on the brand's navy hue, never a neutral near-black (guide 1.0.0).
  'color-surface-page': '#0B1B33',
  'color-surface-raised': '#13243F',
  'color-surface-sunken': '#051228',
  'color-surface-inverse': '#FFFFFF',
  // A navy field on the dark page: #13294B would vanish there (1.07:1).
  'color-surface-inverse-brand': '#314B73',
  'color-semantic-ink': '#FFFFFF',
  'color-semantic-ink-muted': 'rgba(255,255,255,0.6)',
  'color-semantic-ink-muted-aa': 'rgba(255,255,255,0.72)',
  'color-semantic-ink-subtle': 'rgba(255,255,255,0.35)',
  'color-semantic-ink-on-dark': '#141414',
  'color-semantic-interactive': '#93BCFF',
  'color-semantic-interactive-hover': '#BCD6FF',
  'color-semantic-border-hairline': 'rgba(255,255,255,0.14)',
  'color-semantic-border-strong': 'rgba(255,255,255,0.22)',
  'color-semantic-focus-ring': '#2CCDFD',
  'color-dataviz-categorical-light-1': '#1567E9',
  'color-dataviz-categorical-light-2': '#04A1CA',
  'color-dataviz-categorical-light-3': '#13865D',
  'color-dataviz-categorical-light-4': '#6648FB',
  'color-dataviz-categorical-light-5': '#DF2223',
  'color-dataviz-status-light-good': '#2C956B',
  'color-dataviz-status-light-warning': '#FFD100',
  'color-dataviz-status-light-serious': '#DB8904',
  'color-dataviz-status-light-critical': '#E02424',
  'color-app-critical-text': '#FF9B8F',
  'color-app-side': '#051228',
} as const satisfies Partial<Record<TokenName, string>>;

/** Roles this application adds. `tokens.css` does not declare these; `theme.ts` does. */
export const APP_TOKENS = [
  'color-semantic-ink-on-brand',
  'color-semantic-focus-ring-on-brand',
  'color-app-critical-text',
  'color-app-side',
] as const satisfies readonly TokenName[];

/** A CSS custom property, with the light value as its fallback. Use this in styles. */
export function token(name: TokenName): string {
  return `var(--opoc-${name}, ${TOKENS[name]})`;
}

/** The raw light value. Use this only where a library must parse the colour. */
export function literal(name: TokenName): string {
  return TOKENS[name];
}

/** The raw value for one colour scheme. Material UI's palette needs this; styles should use `token`. */
export function literalFor(mode: ColorMode, name: TokenName): string {
  if (mode === 'dark' && name in DARK_TOKENS) {
    return DARK_TOKENS[name as keyof typeof DARK_TOKENS];
  }
  return TOKENS[name];
}

function alphaOf(value: string, alpha: number): string {
  if (!value.startsWith('#')) return value;
  const hex = value.slice(1);
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${red},${green},${blue},${alpha})`;
}

/**
 * An alpha wash of a brand colour, for a chip or cell fill that must sit under ink.
 *
 * Deriving the wash from the token means no component ever types a channel value, and a re-theme
 * changes the wash with the colour it came from. A wash composites over whatever is behind it, so
 * the same 14% cyan reads as a pale tint on white and a deep one on #141414 — which is why these
 * do not need a dark variant, only the ink over them does.
 */
export function tint(name: TokenName, alpha: number): string {
  return alphaOf(TOKENS[name], alpha);
}

/**
 * A colour mixed toward transparency, for a *foreground* mark that must read as its status colour
 * without shouting it.
 *
 * `tint` cannot do this job. It reads the light literal, which is right for a wash sitting under
 * ink — a fill composites over whatever is behind it — and wrong for a mark, which would then stay
 * a light-scheme colour on a dark ground. `color-mix` keeps the custom property, so each scheme
 * softens its own value.
 */
export function soften(name: TokenName, percent: number): string {
  return `color-mix(in srgb, ${token(name)} ${percent}%, transparent)`;
}
